import $ from 'jquery';

const NAME = 'toggleSteps';
const DATA_KEY = `jax.${NAME}`;
const EVENT_KEY = `.${DATA_KEY}`;

const Event = {
  CHANGE: `change${EVENT_KEY}`,
};

const Selector = {
  STEP: '.js-ToggleStep',
  INPUT: 'input,select',
};

const ClassName = {
  READY: 'is-completable',
  COMPLETED: 'is-completed',
};

// If true, inputs will be checked for a `:valid` attribute,
// which may be useful to verify step completion, but will not
// have support in old browsers: https://caniuse.com/#feat=form-validation
const IgnoreValidity = false;

/**
 * Drives a group of "stepped visibility" form fields, which can be used to
 * dynamically populate form field options based on the input from a previous
 * step.
 *
 * This plugin simply marks a given step with a className `is-completed`;
 * CSS is responsible for defining visibility & other state styling. However,
 * this plugin does update the `hidden` property of steps appropriately.
 *
 * Each Step represents a container for a single input, or a single group of
 * checkable (i.e., radio or checkbox) inputs. The last step in the group cannot
 * be "completed", but must still be defined as a `js-ToggleStep` so the plugin
 * is aware of its existence.
 *
 * A step is completed once its contained input is filled out by the user.
 *
 * Note that this does not clear input values at any point, even if clearing
 * the value on Step A's input would normally mean Step B needed to be
 * completed again. It is assumed that in this case, Step B's contents will
 * be replaced (e.g., from the results of an ajax request).
 */

class ToggleSteps {
  constructor(element) {
    this.$container = $(element);
    this.$steps = this.$container.find(Selector.STEP);
    this.$lastStep = this.$steps.last();

    this.$steps.addClass(ClassName.READY);

    this.$steps.on(Event.CHANGE, () => {
      this.updateAllStepVisibility();
    });

    // Update immediately: form may be (re-)loading with user-populated values.
    this.updateAllStepVisibility();
  }

  /**
   * This is the primary interface to update a step's visibility.
   * Each step is checked in succession, since a change in state can cascade
   * if more than two inputs have completed values (A -> B -> C).
   */

  updateAllStepVisibility() {
    this.$steps
      .toArray()
      .some((stepEl) => this._updateStepVisibility($(stepEl)));
  }

  /**
   * Update step visibility based on the state of the given (current) step.
   * The current step is given a css class of "is-completed", which CSS can
   * use to drive visibility changes.
   *
   * If the Step is not completed, then all following steps will be marked
   * as incomplete.
   *
   * @returns {boolean} true if the step has not been completed (or there are
   *  no more steps to check) to indicate that processing need not continue.
   *  false otherwise.
   */

  _updateStepVisibility($currentStep) {
    // If there's no following step, then there's no input to check
    if ($currentStep.is(this.$lastStep)) {
      return true;
    }

    const $input = $currentStep.find(Selector.INPUT);

    if (this._isValid($input)) {
      $currentStep.addClass(ClassName.COMPLETED);
      $currentStep.next(Selector.STEP).removeAttr('hidden');
      return false;
    }

    // Else, mark this & all subsequent steps as not completed.
    const $followingSteps = $currentStep.nextAll(Selector.STEP);
    $currentStep.add($followingSteps).removeClass(ClassName.COMPLETED);
    $followingSteps.attr('hidden', '');
    return true;
  }

  /**
   * An input selection is valid if it matches :valid
   * or, in the case of radio buttons, any one in the group is checked.
   */

  _isValid($input) {
    if ($input.is('[type=radio], [type=checkbox]')) {
      return $input.is(':checked');
    }

    if ($input.length === 1) {
      return $input.val().length > 0 && (IgnoreValidity || $input.is(':valid'));
    }

    // Reaching this means we have undocumented input;
    // defensively default to valid.
    return true;
  }

  /**
   * Interface for using this class as a jQuery plugin.
   *
   * @returns {jQuery}
   */

  static _jQueryInterface() {
    return this.each(function () {
      let data = $(this).data(DATA_KEY);

      if (!data) {
        data = new ToggleSteps(this);
        $(this).data(DATA_KEY, data);
      }
    });
  }
}

$.fn[NAME] = ToggleSteps._jQueryInterface;
$.fn[NAME].Constructor = ToggleSteps;

export default ToggleSteps;
